import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// import "../../../../style/ReplyRaiseTicket.css"; // reuse same CSS or create WarrantyClaimResponse.css
import { apiAxios } from "../../../../utils/apiAxios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_PATH = process.env.REACT_APP_API_PATH_URL;
const APIClaimWarranty = `${API_BASE}/Ticket/GetClaimTickets`; // ✅ Update endpoint as needed
const APIWarranty = `${API_BASE}/Ticket`;
const APIDentistCase = `${API_BASE}/Dentist`;

const WarrantyClaimResponse = () => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const chatEndRef = useRef<any>(null);

  const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
  const [selectedClaimNumber, setSelectedClaimNumber] = useState<string>("");
  const [replies, setReplies] = useState<any[]>([]);
  const [form, setForm] = useState({
    message: "",
    file: null as File | null,
  });

  const [caseInfo, setCaseInfo] = useState<any>(null);
  const [toothList, setToothList] = useState<any[]>([]);
  const [showCaseModal, setShowCaseModal] = useState(false);

  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isQueryFromHub, setIsQueryFromHub] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  // ─── Load Claims ───────────────────────────────────────────────────────────
  const loadClaims = async () => {
    try {
      setLoading(true);
      const res = await apiAxios.get(APIClaimWarranty);
      const formatted = res.data.map((c: any) => ({
        id: c.ticketID,
        claimWarrantyID: c.claimWarrantyID,
        claimNumber: c.claimWarrantyID,
        caseID: c.caseID,
        status: c.warrantyStatus,
        decisionRemarks: c.decisionRemarks,
        approvedRejectedBy: c.approvedRejectedBy
      }));
      setClaims(formatted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClaims();
  }, []);

  // ─── Load Replies ──────────────────────────────────────────────────────────
  const loadReplies = async (ticketId: number) => {
    try {
      const res = await apiAxios.get(`${APIWarranty}/${ticketId}/messages`);
      const formatted = res.data.map((item: any) => ({
        id: item.messageID,
        message: item.message,
        file: item.attachmentPath,
        date: new Date(item.createdDate).toLocaleString("en-GB"),
        senderType: item.senderType,
      }));
      setReplies(formatted);
    } catch (err) {
      console.error(err);
    }
  };

  // ─── Select Claim ──────────────────────────────────────────────────────────
const handleViewReplies = async (claim: any) => {
  const status = (claim.status || "").toUpperCase().trim();

  // Rejected claim: show closed chat, but don't load/open messages
  if (status === "REJECTED") {
    setSelectedClaimId(claim.id);
    setSelectedClaimNumber(claim.claimNumber);
    setReplies([]);
    setForm({ message: "", file: null });

    if (fileRef.current) {
      fileRef.current.value = "";
    }

    return;
  }

  setSelectedClaimId(claim.id);
  setSelectedClaimNumber(claim.claimNumber);
  setForm({ message: "", file: null });

  if (fileRef.current) {
    fileRef.current.value = "";
  }

  await loadReplies(claim.id);
};

  // ─── Accept Claim ──────────────────────────────────────────────────────────
  const handleAccept = async (e: React.MouseEvent, claim: any) => {

    e.stopPropagation(); // prevent row click

    const result = await Swal.fire({
      title: "Accept Warranty Claim?",
      text: `Claim ${claim.claimNumber} will be marked as Accepted.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Accept",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await apiAxios.post(
        `${APIWarranty}/claim-warranty/${claim.id}/approve`
      );
      await Swal.fire({
        title: "Accepted!",
        text: "Warranty claim has been accepted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      await loadClaims();
      // refresh replies if this claim is currently open
      if (selectedClaimId === claim.id) await loadReplies(claim.id);
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error!", text: "Failed to accept claim.", icon: "error" });
    }
  };

  // ─── Reject Claim ──────────────────────────────────────────────────────────
  const handleReject = async (e: React.MouseEvent, claim: any) => {
    e.stopPropagation();

    const { value: remarks } = await Swal.fire({
      title: "Reject Warranty Claim",
      input: "textarea",
      inputLabel: "Remarks",
      inputPlaceholder: "Enter rejection remarks...",
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#dc3545",
      inputValidator: (value) => {
        if (!value) {
          return "Please enter remarks";
        }
      }
    });

    if (!remarks) return;

    try {
      await apiAxios.post(
        `${APIWarranty}/claim-warranty/reject`,
        {
          ticketID: claim.id,
          remarks: remarks
        }
      );

      toast.success("Warranty Rejected");

      await loadClaims();

    } catch (err) {
      console.error(err);
      toast.error("Failed to reject warranty");
    }
  };

  // ─── Input Handlers ────────────────────────────────────────────────────────
  const handleChange = (e: any) => setForm({ ...form, message: e.target.value });
  const handleFile = (e: any) => setForm({ ...form, file: e.target.files[0] });

  // ─── Send Reply ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.message.trim() && !form.file) return;
    try {
      const formData = new FormData();
      formData.append("TicketID", selectedClaimId!.toString());
      formData.append("Message", form.message);
      formData.append("IsQueryFromHub", isQueryFromHub.toString());
      if (form.file) formData.append("file", form.file);

      await apiAxios.post(`${APIWarranty}/admin/reply`, formData);
      toast.success("Reply sent");
      setForm({ message: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
      await loadReplies(selectedClaimId!);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send reply");
    }
  };

  // ─── Case Detail Modal ─────────────────────────────────────────────────────
  const openCaseDetail = async (caseId: number) => {
    try {
      const res = await apiAxios.post(`${APIDentistCase}/GetReuploadScanDetail`, { CaseID: caseId });
      const data = res.data.singleData || res.data;
      if (!data || data.length === 0) return;
      setCaseInfo(data[0]);
      setToothList(data);
      setShowCaseModal(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load case");
    }
  };

  // ─── Close Claim ───────────────────────────────────────────────────────────
  const handleCloseClaim = async () => {
    if (!selectedClaimId) return;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to close this warranty claim!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, close it!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await apiAxios.post(`${APIWarranty}/${selectedClaimId}/close`, { id: selectedClaimId });
      await Swal.fire({
        title: "Closed!",
        text: "Warranty claim has been closed successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
      await loadClaims();
      setSelectedClaimId(null);
      setReplies([]);
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error!", text: "Failed to close claim.", icon: "error" });
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  };

  // ─── Auto Scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies]);

  // ─── Table Columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Claim No.",
      selector: (row: any) => row.claimNumber,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row: any) => {
        const colorMap: Record<string, string> = {
          Accepted: "bg-success",
          Rejected: "bg-danger",
          Pending: "bg-warning text-dark",
        };

        const badgeClass = colorMap[row.status] || "bg-info";

        return (
          <span
            title={row.status}
            className={`badge raise-ticket-badge ${badgeClass}`}
            style={{
              display: "inline-block",
              maxWidth: "160px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.status}
          </span>
        );
      },
      minWidth: "210px",
    },
    {
      name: "Case",
      cell: (row: any) => (
        <span
          style={{ color: "#0d6efd", cursor: "pointer", fontWeight: 500 }}
          onClick={(e) => { e.stopPropagation(); openCaseDetail(row.caseID); }}
        >
          TZ{row.caseID}
        </span>
      ),
    },
    {
      name: "Action",
      cell: (row: any) => {
        const currentStatus = (row.status || "").toUpperCase().trim();

        const hideActionsFor = ["APPROVED", "REJECTED", "REUPLOAD_RECEIVED"];
        const shouldHideActions = hideActionsFor.includes(currentStatus);

        if (shouldHideActions) {
          return (
            <div style={{ minWidth: "160px" }}>
              <span className="text-muted fw-semibold" style={{ fontSize: 12 }}>
                No Action
              </span>
            </div>
          );
        }

        return (
          <div
            className="d-flex gap-1"
            style={{ minWidth: "160px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="btn btn-sm btn-success py-0 px-2"
              style={{ fontSize: 12 }}
              title="Accept"
              onClick={(e) => handleAccept(e, row)}
            >
              <i className="bi bi-check-lg"></i> Accept
            </button>

            <button
              className="btn btn-sm btn-danger py-0 px-2"
              style={{ fontSize: 12 }}
              title="Reject"
              onClick={(e) => handleReject(e, row)}
            >
              <i className="bi bi-x-lg"></i> Reject
            </button>
          </div>
        );
      },
      minWidth: "190px",
      grow: 1.2,
      ignoreRowClick: true,
    }
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  const currentClaim = claims.find((c) => c.id === selectedClaimId);
  const currentStatus = (currentClaim?.status || "").toUpperCase().trim();
const isClosed =
  currentStatus === "CLOSED" ||
  currentStatus === "APPROVED" ||
  currentStatus === "REJECTED";
  return (
    <div className="container-fluid mt-3 reply-ticket-page">
      <div className="row">

        {/* ── LEFT: Claims Table ── */}
        <div className="col-md-5">
          <div className="premium-card chat-card shadow-sm">
            <div className="card-header">Warranty Claims</div>
            <DataTable
              columns={columns}
              data={claims}
              progressPending={loading}
              onRowClicked={(row) => handleViewReplies(row)}
              pagination
              paginationComponentOptions={{ noRowsPerPage: true }}
              fixedHeader
              fixedHeaderScrollHeight="400px"
            />
          </div>
        </div>

        {/* ── RIGHT: Chat Panel ── */}
        <div className="col-md-7">
          <div className="chat-container premium-card raise-ticket-premium-card chat-card">

            {/* Header */}
            <div className="chat-header d-flex justify-content-between align-items-center">
              <span>
                {selectedClaimId
                  ? `Warranty Claim - ${selectedClaimNumber}`
                  : "Select a warranty claim"}
              </span>
              {selectedClaimId && !isClosed ? (
                <button
                  className="btn btn-sm btn-light text-danger"
                  onClick={handleCloseClaim}
                >
                  Close
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setSelectedClaimId(null);
                    setReplies([]);
                  }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>

            {/* Chat Body */}
            <div className="chat-body">
              {replies.length === 0 && (
                <div className="text-center text-muted mt-3">No messages</div>
              )}

              {replies.map((msg) => {
                const hasMessage = msg.message?.trim();
                const hasFile = msg.file && msg.file !== "";
                if (!hasMessage && !hasFile) return null;

                const isSent = msg.senderType === "Central Hub";
                const isUnread = !msg.seen && !isSent;

                return (
                  <div
                    key={msg.id}
                    className={`chat-message ${isSent ? "sent" : "received"} ${isUnread ? "unread" : ""}`}
                  >
                    <div className="chat-bubble">
                      <div>{msg.message}</div>

                      {msg.file && (
                        msg.file.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                          <img
                            src={`${API_PATH}${msg.file}`}
                            alt="img"
                            className="chat-img"
                          />
                        ) : (
                          <a
                            href={`${API_PATH}${msg.file}`}
                            target="_blank"
                            rel="noreferrer"
                            className="chat-file"
                          >
                            📎 File
                          </a>
                        )
                      )}

                      <div className="chat-meta">
                        <span className="chat-time-hover">{msg.date}</span>
                        {isSent && (
                          <span className={`tick ${msg.seen ? "seen" : msg.delivered ? "delivered" : ""}`}>
                            ✔✔
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {isTyping && (
                <div className="chat-message received">
                  <div className="chat-bubble typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef}></div>
            </div>

            {/* Closed Banner */}
            {selectedClaimId && isClosed && (
              <div className="chat-closed">🔒 This warranty claim is closed</div>
            )}

            {/* Footer */}
            {selectedClaimId && !isClosed && (
              <div className="chat-footer">
                <div className="chat-top-bar">
                  <label className="chat-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isQueryFromHub}
                      onChange={(e) => setIsQueryFromHub(e.target.checked)}
                    />
                    <span>Ask input from Dentist</span>
                  </label>
                </div>

                <div className="chat-input-row">
                  <div className="chat-input-wrapper position-relative">
                    <input
                      type="text"
                      className="form-control chat-input"
                      placeholder="Type a message..."
                      value={form.message}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && form.message.trim()) {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                    />
                  </div>

                  <input
                    type="file"
                    ref={fileRef}
                    hidden
                    id="warrantyFileInput"
                    onChange={handleFile}
                  />

                  <label htmlFor="warrantyFileInput" className="icon-btn">
                    <i className="bi bi-paperclip"></i>
                  </label>

                  <button className="send-btn" onClick={handleSubmit}>
                    <i className="bi bi-send-fill"></i>
                  </button>
                </div>

                {form.file && (
                  <div className="file-preview">
                    {form.file.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(form.file)}
                        alt="preview"
                        className="preview-img"
                      />
                    ) : (
                      <span>📎 {form.file.name}</span>
                    )}
                    <i
                      className="bi bi-x-circle-fill remove-file"
                      onClick={() => setForm((prev) => ({ ...prev, file: null }))}
                    ></i>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Case Detail Modal ── */}
      {showCaseModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <div className="modal-header">
              <h5>Case Details</h5>
              <button
                onClick={() => setShowCaseModal(false)}
                className="custom-close-btn raise-ticket-closebtn btn-close"
              >
                <i className="bi bi-x customcross"></i>
              </button>
            </div>

            <div className="modal-body">
              {caseInfo && (
                <div className="mb-3 border-bottom pb-2">
                  <div className="carse-id-text"><strong>Case ID:</strong> TZ{caseInfo.caseID}</div>
                  <div className="carse-id-text"><strong>Patient:</strong> {caseInfo.patientName}</div>
                  <div className="carse-id-text">
                    <strong>Status:</strong>
                    <span className="badge bg-warning ms-2">{caseInfo.dentistTag}</span>
                  </div>
                  <div className="carse-id-text">
                    <strong>ETA:</strong> {formatDateTime(caseInfo.expectedEta)}
                  </div>
                </div>
              )}

              <div>
                <h6>Tooth Details</h6>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Tooth No</th>
                      <th>Shade</th>
                      <th>Material</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toothList.map((t, i) => (
                      <tr key={i}>
                        <td>{t.toothNumber}</td>
                        <td>{t.shade}</td>
                        <td>{t.materialName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarrantyClaimResponse;
